import React, { useEffect, useState, useCallback } from "react";
import styles from "./BasketDrawer.module.scss";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchBasketData,
  incrementQuantity,
  decrementQuantity,
  removeItem,
  clearBasket,
  setIsBasketOpen,
} from "../../../redux/Slices/BasketSlice";
import BasketServices from "../../basket/basketService/BasketService";
import ReactLoading from "react-loading";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { IoCloseOutline } from "react-icons/io5";
import {
  FiTrash2,
  FiPlus,
  FiMinus,
  FiShoppingBag,
  FiArrowRight,
} from "react-icons/fi";
import placeholderImage from "../../../assets/images/placeholder.jpg";

const TrashIcon = FiTrash2 as any;
const CloseIcon = IoCloseOutline as any;
const ShoppingBagIcon = FiShoppingBag as any;
const MinusIcon = FiMinus as any;
const PlusIcon = FiPlus as any;
const ArrowRightIcon = FiArrowRight as any;

interface BasketDrawerProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const formatPrice = (value: number | string | undefined | null): string => {
  if (value === undefined || value === null || value === "") return "0.00";
  const numericValue = Number(String(value).replace(/,/g, "").trim());
  if (isNaN(numericValue)) return String(value);
  return numericValue.toFixed(2);
};

export default function BasketDrawer({
  isOpen,
  setIsOpen,
}: BasketDrawerProps): JSX.Element | null {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [loading, setLoading] = useState<boolean>(false);
  const [itemLoadingState, setItemLoadingState] = useState<{
    [key: number]: boolean;
  }>({});

  const {
    basketItems,
    totalPrice,
    totalDiscountPrice,
    discountedPrice,
  } = useSelector((state: RootState) => state.basket);

  const selectedLanguage = useSelector(
    (state: RootState) => state.language.selectedLanguage?.code
  );
  const translations = useSelector(
    (state: RootState) => state.translations.data
  );

  const handleCloseDrawer = useCallback(() => {
    dispatch(setIsBasketOpen(false));
    setIsOpen(false);
  }, [setIsOpen, dispatch]);

  useEffect(() => {
    if (isOpen) {
      dispatch(setIsBasketOpen(true));
    }
    return () => {
      dispatch(setIsBasketOpen(false));
    };
  }, [isOpen, dispatch]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCloseDrawer();
      }
    },
    [handleCloseDrawer]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchBasketData());
  }, [isOpen, selectedLanguage, dispatch]);

  const handleNavigateToBasket = () => {
    handleCloseDrawer();
    navigate("/basket");
  };

  const handleIncrementQuantity = async (item: any) => {
    const pId = Number(item.product_id || item.product?.id || item.id);
    const vId = item.variant_id || item.product_variant_id || item.variant?.id;
    const itemId = item.basket_id || item.id || pId;
    const currentQuantity = Number(item.quantity) || 1;

    setItemLoadingState((prev) => ({ ...prev, [itemId]: true }));
    try {
      await BasketServices.UpdateBasket({
        product_id: pId,
        quantity: currentQuantity + 1,
        ...(vId ? { variant_id: Number(vId) } : {}),
      });
      dispatch(incrementQuantity(itemId));
      await dispatch(fetchBasketData());
    } catch (err: any) {
      console.error("Increment error:", err);
      let errorMsg = translations?.error || "Məhsul artırılarkən xəta baş verdi";
      if (err instanceof AxiosError) {
        const errData = err.response?.data;
        if (errData?.errors) {
          const firstKey = Object.keys(errData.errors)[0];
          if (
            firstKey &&
            Array.isArray(errData.errors[firstKey]) &&
            errData.errors[firstKey].length > 0
          ) {
            errorMsg = errData.errors[firstKey][0];
          }
        } else if (errData?.message) {
          errorMsg = errData.message;
        }
      }
      toast.error(errorMsg);
    } finally {
      setItemLoadingState((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleDecrementQuantity = async (item: any) => {
    const pId = Number(item.product_id || item.product?.id || item.id);
    const vId = item.variant_id || item.product_variant_id || item.variant?.id;
    const itemId = item.basket_id || item.id || pId;
    const currentQuantity = Number(item.quantity) || 1;
    const minOrderQty = Math.max(
      1,
      Number(item.min_order_quantity ?? item.product?.min_order_quantity) || 1
    );

    if (currentQuantity <= minOrderQty) {
      return;
    }
    setItemLoadingState((prev) => ({ ...prev, [itemId]: true }));
    try {
      await BasketServices.UpdateBasket({
        product_id: pId,
        quantity: currentQuantity - 1,
        ...(vId ? { variant_id: Number(vId) } : {}),
      });
      dispatch(decrementQuantity(itemId));
      await dispatch(fetchBasketData());
    } catch (err: any) {
      console.error("Decrement error:", err);
      let errorMsg = translations?.error || "Məhsul azaldılarkən xəta baş verdi";
      if (err instanceof AxiosError) {
        const errData = err.response?.data;
        if (errData?.errors) {
          const firstKey = Object.keys(errData.errors)[0];
          if (
            firstKey &&
            Array.isArray(errData.errors[firstKey]) &&
            errData.errors[firstKey].length > 0
          ) {
            errorMsg = errData.errors[firstKey][0];
          }
        } else if (errData?.message) {
          errorMsg = errData.message;
        }
      }
      toast.error(errorMsg);
    } finally {
      setItemLoadingState((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleDeleteItem = async (item: any) => {
    const pId = Number(item.product_id || item.product?.id || item.id);
    const vId = item.variant_id || item.product_variant_id || item.variant?.id;
    const itemId = item.basket_id || item.id || pId;

    setItemLoadingState((prev) => ({ ...prev, [itemId]: true }));
    try {
      await BasketServices.DeleteBasket(pId, vId ? Number(vId) : undefined);
      dispatch(removeItem(itemId));
      await dispatch(fetchBasketData());
    } catch (err: any) {
      console.error("Delete item error:", err);
      let errorMsg = translations?.error || "Məhsul silinərkən xəta baş verdi";
      if (err instanceof AxiosError) {
        const errData = err.response?.data;
        if (errData?.message) {
          errorMsg = errData.message;
        }
      }
      toast.error(errorMsg);
    } finally {
      setItemLoadingState((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleClearBasket = async () => {
    setLoading(true);
    try {
      await BasketServices.ClearBasket();
      dispatch(clearBasket());
      await dispatch(fetchBasketData());
    } catch (err: any) {
      console.error("Clear basket error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getItemTitle = (item: any): string => {
    if (item?.name && typeof item.name === "string" && item.name.trim() !== "") {
      return item.name;
    }
    if (
      item?.product?.name &&
      typeof item.product.name === "string" &&
      item.product.name.trim() !== ""
    ) {
      return item.product.name;
    }
    if (item?.model_code && typeof item.model_code === "string" && item.model_code.trim() !== "") {
      return item.model_code;
    }
    if (item?.slug && typeof item.slug === "string" && item.slug.trim() !== "") {
      return item.slug;
    }
    return translations?.product || "Məhsul";
  };

  const getItemAttributes = (item: any): string[] => {
    return Array.isArray(item?.attributes) ? item.attributes : [];
  };

  if (!isOpen) return null;

  const totalQuantity = basketItems.reduce(
    (acc: number, item: any) => acc + (Number(item?.quantity) || 1),
    0
  );

  const hasDiscount = Number(discountedPrice) > 0;
  const finalPriceToDisplay =
    Number(totalDiscountPrice) > 0
      ? totalDiscountPrice
      : totalPrice;

  return (
    <div
      className={styles.overlay}
      onClick={handleCloseDrawer}
      aria-modal="true"
      role="dialog"
    >
      <div
        className={styles.cart_drawer}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <header className={styles.drawer_header}>
          <div className={styles.title_group}>
            <h2>{translations?.basket || "Səbət"}</h2>
            {basketItems.length > 0 && (
              <span className={styles.items_count_badge}>
                {totalQuantity} {translations?.items_count || "ədəd"}
              </span>
            )}
          </div>

          <div className={styles.header_actions}>
            {basketItems.length > 0 && (
              <button
                type="button"
                className={styles.clear_all_btn}
                onClick={handleClearBasket}
                disabled={loading}
                aria-label={translations?.clear_all || "Hamısını sil"}
                tabIndex={0}
              >
                <TrashIcon />
                <span>{translations?.clear_all || "Hamısını sil"}</span>
              </button>
            )}

            <button
              type="button"
              className={styles.close_btn}
              onClick={handleCloseDrawer}
              aria-label={translations?.close || "Bağla"}
              tabIndex={0}
            >
              <CloseIcon />
            </button>
          </div>
        </header>

        {/* CONTENT */}
        {basketItems?.length === 0 ? (
          <div className={styles.empty_cart}>
            <div className={styles.empty_icon_container}>
              <ShoppingBagIcon />
            </div>
            <h3 className={styles.empty_title}>
              {translations?.empty_message || "Səbətiniz boşdur"}
            </h3>
            <p className={styles.empty_desc}>
              {translations?.empty_cart_desc ||
                "Səbətinizdə hazırda heç bir məhsul yoxdur. Alış-verişə davam edərək yeni məhsullar əlavə edə bilərsiniz."}
            </p>
            <button
              type="button"
              className={styles.continue_shopping}
              onClick={handleCloseDrawer}
              tabIndex={0}
            >
              {translations?.continue_shopping || "Alış-verişə davam et"}
            </button>
          </div>
        ) : (
          <>
            <div className={styles.cart_items}>
              {loading ? (
                <div className={styles.loading_items}>
                  <ReactLoading
                    type={"spin"}
                    color={"var(--color-brand-primary, #344733)"}
                    height={36}
                    width={36}
                  />
                  <span>{translations?.loading || "Yüklənir..."}</span>
                </div>
              ) : (
                basketItems.map((item) => {
                  const isItemBusy = itemLoadingState[item.id] || false;
                  const itemTitle = getItemTitle(item);
                  const itemAttributes = getItemAttributes(item);
                  const rawItem = item as any;
                  const currentQty = Number(item.quantity) || 1;
                  const minOrderQty = Math.max(
                    1,
                    Number(rawItem.min_order_quantity ?? rawItem.product?.min_order_quantity) || 1
                  );
                  const isMinReached = currentQty <= minOrderQty;
                  const variantId =
                    item.variant_id || rawItem.product_variant_id || rawItem.variant?.id;
                  const productSlug =
                    item.slug || rawItem.product?.slug || item.product_id || item.id;
                  const productDetailUrl = variantId
                    ? `/productDetail/${productSlug}?variant=${variantId}`
                    : `/productDetail/${productSlug}`;

                  return (
                    <div key={item.id} className={styles.cart_item}>
                      <Link
                        to={productDetailUrl}
                        state={{ variantId }}
                        onClick={handleCloseDrawer}
                        className={styles.cart_item_image_wrapper}
                        aria-label={itemTitle}
                        tabIndex={0}
                      >
                        <img
                          src={item.variant_image || placeholderImage}
                          alt={itemTitle}
                          className={styles.cart_item_image}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = placeholderImage;
                          }}
                        />
                      </Link>

                      <div className={styles.cart_item_details}>
                        <div className={styles.cart_name_price}>
                          <Link
                            to={productDetailUrl}
                            state={{ variantId }}
                            onClick={handleCloseDrawer}
                            className={styles.title_link}
                            aria-label={itemTitle}
                            tabIndex={0}
                          >
                            <p className={styles.title} title={itemTitle}>
                              {itemTitle}
                            </p>
                            {itemAttributes.length > 0 && (
                              <div className={styles.product_attributes_list}>
                                {itemAttributes.map((attr, index) => (
                                  <span
                                    key={index}
                                    className={styles.product_attribute_badge}
                                  >
                                    {attr}
                                  </span>
                                ))}
                              </div>
                            )}
                          </Link>

                          <div className={styles.price_box}>
                            {item.discount_price && Number(String(item.discount_price).replace(/,/g, "").trim()) > 0 ? (
                              <>
                                <p className={styles.prev_price}>
                                  {item.price} AZN
                                </p>
                                <p className={styles.current_price}>
                                  {item.discount_price} AZN
                                </p>
                              </>
                            ) : (
                              <p className={styles.current_price}>
                                {item.price} AZN
                              </p>
                            )}
                          </div>
                        </div>

                        <div className={styles.amount_buttons}>
                          {/* Quantity stepper + Min order hint */}
                          <div className={styles.stepper_group}>
                            <div className={styles.quantity_stepper}>
                              <button
                                type="button"
                                onClick={() => handleDecrementQuantity(item)}
                                disabled={isItemBusy || isMinReached}
                                aria-label="Azalt"
                                title={
                                  isMinReached
                                    ? `${translations?.min_order_quantity || "Minimum sifariş sayı:"} ${minOrderQty}`
                                    : "Azalt"
                                }
                                tabIndex={0}
                              >
                                <MinusIcon />
                              </button>
                              <span className={styles.quantity_text}>
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleIncrementQuantity(item)}
                                disabled={isItemBusy}
                                aria-label="Artır"
                                tabIndex={0}
                              >
                                <PlusIcon />
                              </button>
                            </div>
                            <span
                              className={`${styles.min_order_hint} ${
                                minOrderQty > 1 ? styles.min_order_hint_accent : ""
                              }`}
                            >
                              {translations?.min_order || "Min."} {minOrderQty}{" "}
                              {translations?.piece || "ədəd"}
                            </span>
                          </div>

                          {/* Quick action buttons */}
                          <div className={styles.buttons_container}>
                            <button
                              type="button"
                              className={`${styles.action_icon_btn} ${styles.delete_btn}`}
                              onClick={() => handleDeleteItem(item)}
                              disabled={isItemBusy}
                              aria-label="Səbətdən sil"
                              tabIndex={0}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* SUMMARY & CHECKOUT FOOTER */}
            <footer className={styles.drawer_footer}>
              <div className={styles.cart_summary}>
                {/*
                <div className={styles.summary_row}>
                  <span className={styles.label}>
                    {translations?.total || "Cəmi"}:
                  </span>
                  <span className={styles.value}>
                    {formatPrice(totalPrice)} AZN
                  </span>
                </div>

                {hasDiscount && (
                  <div className={`${styles.summary_row} ${styles.discount}`}>
                    <span className={styles.label}>
                      {translations?.discount || "Endirim"}:
                    </span>
                    <span className={styles.value}>
                      -{formatPrice(discountedPrice)} AZN
                    </span>
                  </div>
                )}

                <div className={styles.summary_divider} />
                */}

                <div
                  className={`${styles.summary_row} ${styles.total_final}`}
                >
                  <span className={styles.label}>
                    {translations?.final_price || "Yekun məbləğ:"}
                  </span>
                  <span className={styles.value}>
                    {formatPrice(finalPriceToDisplay)} AZN
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleNavigateToBasket}
                className={styles.view_cart_button}
                tabIndex={0}
              >
                <span>{translations?.view_cart || "Səbətə keç"}</span>
                <ArrowRightIcon />
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
